// ============================================================================
// Transfer Orders vocabulary — labels, the two fixed directions and their stable receiving-warehouse
// sets, plus the five-status meta shared by the cards and the drill-down drawer (so a status reads
// and colours the same everywhere). Mirrors the summary's `transfer_orders` block
// (FirstMileSummaryResponse in control_tower.api.ts).
//
// The direction → dock structure is HARD-CODED because the backend guarantees it stable: blr_to_hyd
// only ever lands at Hyderabad, hyd_to_blr at one of the three Bengaluru docks. Zero cards are
// rendered, not hidden.
// ============================================================================
import { SwapOutlined } from "@ant-design/icons";
import React from "react";
import {
  TransferDirection,
  TransferDockKey,
  TransferOrderCounts,
  TransferStatusKey,
} from "./control_tower.api";

// Receiving-warehouse labels. Transfers keep Horamavu and Warehouse Now separate (unlike the receive
// sections, which fold them into one card).
export const TRANSFER_DOCK_LABEL: Record<TransferDockKey, string> = {
  hyderabad: "Hyderabad",
  horamavu: "Horamavu",
  rebound: "Rebound",
  whnow: "Warehouse Now",
};

export const TRANSFER_DIRECTION_LABEL: Record<TransferDirection, string> = {
  blr_to_hyd: "Bengaluru → Hyderabad",
  hyd_to_blr: "Hyderabad → Bengaluru",
};

// The "all receiving warehouses" label per direction — named by the destination city, used on the
// PDF when no single dock is selected (the aggregated "All" view).
export const TRANSFER_ALL_WAREHOUSES_LABEL: Record<TransferDirection, string> = {
  blr_to_hyd: "All Hyderabad Warehouses",
  hyd_to_blr: "All Bangalore Warehouses",
};

export interface TransferDirectionMeta {
  key: TransferDirection;
  help: string;
  docks: TransferDockKey[]; // the stable receiving-warehouse set — rendered even at zero
  // When true, the direction shows ONE card summing every dock; the drill-down drawer then breaks
  // the docks out as tabs. When false, one card per dock (a single-dock direction is already one).
  aggregate?: boolean;
  aggregateLabel?: string; // the summed card's title (aggregate directions only)
}

export const TRANSFER_DIRECTIONS: TransferDirectionMeta[] = [
  {
    key: "blr_to_hyd",
    help: "Stock transferred from the Bengaluru warehouses, received at Hyderabad.",
    docks: ["hyderabad"],
  },
  {
    key: "hyd_to_blr",
    help: "Stock transferred from Hyderabad, received across the Bengaluru warehouses (Horamavu / Rebound / Warehouse Now). One card for the total; the drill-down splits it by warehouse.",
    docks: ["horamavu", "rebound", "whnow"],
    aggregate: true,
    aggregateLabel: "All warehouses",
  },
];

// The three LIFECYCLE statuses, in order — these sum to total_shipments and make up the lifecycle
// bar. `error` is NOT here (it's an overlay, rendered as a separate badge). `color` is the antd Tag
// colour used on the status chip and lifecycle blocks.
export const TRANSFER_STATUS_ORDER: TransferStatusKey[] = ["yet_to_pick", "in_transit", "transferred"];

export const TRANSFER_STATUS_META: Record<TransferStatusKey, { label: string; color: string }> = {
  yet_to_pick: { label: "Yet to Pick", color: "default" },
  in_transit: { label: "In Transit", color: "blue" },
  transferred: { label: "Transferred", color: "green" },
};

// A zero cell for a receiving warehouse the summary omitted (no rows in scope).
export const TRANSFER_EMPTY_COUNTS: TransferOrderCounts = {
  total_shipments: 0,
  yet_to_pick: 0,
  in_transit: 0,
  transferred: 0,
  error: 0,
  total_quantity: 0,
  total_cargo_weight_kg: 0,
};

// Coerce a value (number or Django decimal string) to a number, or null.
const toNum = (v: number | string | null | undefined): number | null => {
  const n = typeof v === "number" ? v : v != null && v !== "" ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

// A weight as a label, e.g. "15.0 kg" (always ≥1 decimal so a round value reads as a weight).
export const fmtTransferWeight = (v: number | string | null | undefined): string => {
  const n = toNum(v);
  return n != null
    ? `${n.toLocaleString("en-IN", { minimumFractionDigits: 1, maximumFractionDigits: 2 })} kg`
    : "—";
};

// A quantity as a label, e.g. "42" or "5.5" — up to 2 decimals, no unit.
export const fmtTransferQty = (v: number | string | null | undefined): string => {
  const n = toNum(v);
  return n != null ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "—";
};

// Sum several warehouse cells into one — the aggregated card's counts. Absent cells count as zero.
export const sumTransferCounts = (
  cells: Array<TransferOrderCounts | undefined>
): TransferOrderCounts =>
  cells.reduce<TransferOrderCounts>(
    (acc, c) => {
      if (!c) return acc;
      acc.total_shipments += c.total_shipments;
      acc.yet_to_pick += c.yet_to_pick;
      acc.in_transit += c.in_transit;
      acc.transferred += c.transferred;
      acc.error += c.error;
      acc.total_quantity = Number(acc.total_quantity) + (toNum(c.total_quantity) ?? 0);
      acc.total_cargo_weight_kg =
        Number(acc.total_cargo_weight_kg) + (toNum(c.total_cargo_weight_kg) ?? 0);
      return acc;
    },
    { ...TRANSFER_EMPTY_COUNTS }
  );

// The count behind a clicked figure on a cell: the error overlay, a lifecycle status, or the total.
export const transferFigure = (
  cell: TransferOrderCounts | undefined,
  status?: TransferStatusKey,
  hasError?: boolean
): number => (!cell ? 0 : hasError ? cell.error : status ? cell[status] : cell.total_shipments);

export const TRANSFER_SECTION_ICON = <SwapOutlined style={{ color: "#eb2f96" }} />;
